/**
 * Escalation Enhanced Component
 * Unique Properties: Path builder, notification templates, SLA tracking, routing rules
 */

import { Alert, Badge, Button, Card, Container, Grid, Group, NumberInput, Paper, Select, Stack, Tabs, Text, TextInput, Textarea, Timeline, Title } from '@mantine/core';
import { IconAlertTriangle, IconBell, IconClock, IconRoute } from '@tabler/icons-react';
import React, { useEffect, useRef, useState } from 'react';
import { TabulatorFull } from 'tabulator-tables';
import 'tabulator-tables/dist/css/tabulator_midnight.min.css';
import WorkflowSearchMenu from './WorkflowSearchMenu';
import { useEscalationEvents } from '../hooks/useSubscriptions';

interface EscalationProps {
    data?: any;
    mode?: 'configure' | 'monitor' | 'test';
}

export const EscalationEnhanced: React.FC<EscalationProps> = ({ data = {}, mode = 'configure' }) => {
    const [activeTab, setActiveTab] = useState('paths');
    const tableRef = useRef<HTMLDivElement>(null);
    
    // Real-time subscription for escalation events
    const escalationEvents = useEscalationEvents(
        'test',
        {
            onData: (data) => {
                console.log('Escalation triggered:', data);
                // Update the escalation tracking table if available
                // Could also trigger notifications or update UI state
            }
        },
        { enabled: true, showWarnings: true, showInfo: true }
    );

    useEffect(() => {
        if (tableRef.current) {
            new TabulatorFull(tableRef.current, {
                data: [],
                layout: 'fitColumns',
                movableRows: true,
                columns: [
                    { title: 'Level', field: 'level' },
                    { title: 'Assignee', field: 'assignee', editor: 'input' },
                    { title: 'Wait Time', field: 'wait_time', editor: 'number' },
                    { title: 'Escalation', field: 'escalation' },
                    { title: 'Action', field: 'action' }
                ]
            });
        }
    }, []);

    return (
        <Container size="xl">
            <Paper shadow="sm" p="lg">
                <Title order={2} mb="lg">Escalation Management</Title>
                <Tabs value={activeTab} onChange={(v) => setActiveTab(v ?? '')}>
                    <Tabs.List>
                        <Tabs.Tab value="paths" leftSection={<IconRoute size={16} />}>Paths</Tabs.Tab>
                        <Tabs.Tab value="notifications" leftSection={<IconBell size={16} />}>Notifications</Tabs.Tab>
                        <Tabs.Tab value="sla" leftSection={<IconClock size={16} />}>SLA</Tabs.Tab>
                        <Tabs.Tab value="alerts" leftSection={<IconAlertTriangle size={16} />}>Active Alerts</Tabs.Tab>
                    </Tabs.List>
                    <Tabs.Panel value="paths">
                        <Stack mt="md">
                            <Alert color="orange">
                                Define escalation paths for different severity levels
                            </Alert>
                            <Card withBorder>
                                <Title order={5} mb="md">Escalation Path</Title>
                                <Timeline active={1}>
                                    <Timeline.Item title="Level 1 - Support Agent">
                                        <Text size="sm" color="dimmed">Wait time: 15 minutes</Text>
                                        <Text size="sm">Notification: Email</Text>
                                    </Timeline.Item>
                                    <Timeline.Item title="Level 2 - Team Lead">
                                        <Text size="sm" color="dimmed">Wait time: 30 minutes</Text>
                                        <Text size="sm">Notification: Email + SMS</Text>
                                    </Timeline.Item>
                                    <Timeline.Item title="Level 3 - Manager">
                                        <Text size="sm" color="dimmed">Wait time: 1 hour</Text>
                                        <Text size="sm">Notification: Email + SMS + Phone</Text>
                                    </Timeline.Item>
                                    <Timeline.Item title="Level 4 - Executive">
                                        <Text size="sm" color="dimmed">Wait time: 2 hours</Text>
                                        <Text size="sm">Notification: All channels</Text>
                                    </Timeline.Item>
                                </Timeline>
                            </Card>
                            <div ref={tableRef} />
                        </Stack>
                    </Tabs.Panel>
                    <Tabs.Panel value="notifications">
                        <Stack mt="md">
                            <Grid>
                                <Grid.Col span={6}>
                                    <Card withBorder>
                                        <Title order={5} mb="md">Email Template</Title>
                                        <Stack gap="xs">
                                            <TextInput label="Subject" defaultValue="[URGENT] Escalation Required" />
                                            <Textarea
                                                label="Body"
                                                minRows={5}
                                                defaultValue="An issue requires your immediate attention..."
                                            />
                                            <Button>Save Template</Button>
                                        </Stack>
                                    </Card>
                                </Grid.Col>
                                <Grid.Col span={6}>
                                    <Card withBorder>
                                        <Title order={5} mb="md">SMS Template</Title>
                                        <Stack gap="xs">
                                            <Textarea
                                                label="Message"
                                                maxRows={3}
                                                defaultValue="ALERT: Issue #{{id}} needs attention. Reply YES to acknowledge."
                                            />
                                            <Button>Save Template</Button>
                                        </Stack>
                                    </Card>
                                </Grid.Col>
                            </Grid>
                        </Stack>
                    </Tabs.Panel>
                    <Tabs.Panel value="sla">
                        <Stack mt="md">
                            <Grid>
                                <Grid.Col span={4}>
                                    <Card withBorder>
                                        <Text size="sm" color="dimmed">Critical Issues</Text>
                                        <Title order={3}>1 hour</Title>
                                        <Badge color="red" mt="xs">Response Time</Badge>
                                    </Card>
                                </Grid.Col>
                                <Grid.Col span={4}>
                                    <Card withBorder>
                                        <Text size="sm" color="dimmed">High Priority</Text>
                                        <Title order={3}>4 hours</Title>
                                        <Badge color="orange" mt="xs">Response Time</Badge>
                                    </Card>
                                </Grid.Col>
                                <Grid.Col span={4}>
                                    <Card withBorder>
                                        <Text size="sm" color="dimmed">Normal Priority</Text>
                                        <Title order={3}>24 hours</Title>
                                        <Badge color="blue" mt="xs">Response Time</Badge>
                                    </Card>
                                </Grid.Col>
                            </Grid>
                            <Card withBorder>
                                <Title order={5} mb="md">Configure SLA</Title>
                                <Stack gap="xs">
                                    <Select
                                        label="Priority Level"
                                        data={['Critical', 'High', 'Medium', 'Low']}
                                    />
                                    <NumberInput
                                        label="Response Time (hours)"
                                        min={0}
                                        defaultValue={4}
                                    />
                                    <NumberInput
                                        label="Resolution Time (hours)"
                                        min={0}
                                        defaultValue={24}
                                    />
                                    <Button>Save SLA</Button>
                                </Stack>
                            </Card>
                        </Stack>
                    </Tabs.Panel>
                    <Tabs.Panel value="alerts">
                        <Stack mt="md">
                            <Alert color="red" icon={<IconAlertTriangle />}>
                                3 active escalations require attention
                            </Alert>
                            <Grid>
                                <Grid.Col span={4}>
                                    <Card withBorder>
                                        <Group justify="space-between" mb="xs">
                                            <Badge color="red">Critical</Badge>
                                            <Text size="xs" color="dimmed">10 min ago</Text>
                                        </Group>
                                        <Text fw={500}>Database Connection Lost</Text>
                                        <Text size="sm" color="dimmed">Currently at Level 2</Text>
                                        <Button size="xs" mt="xs">Acknowledge</Button>
                                    </Card>
                                </Grid.Col>
                                <Grid.Col span={4}>
                                    <Card withBorder>
                                        <Group justify="space-between" mb="xs">
                                            <Badge color="orange">High</Badge>
                                            <Text size="xs" color="dimmed">25 min ago</Text>
                                        </Group>
                                        <Text fw={500}>API Response Slow</Text>
                                        <Text size="sm" color="dimmed">Currently at Level 1</Text>
                                        <Button size="xs" mt="xs">Acknowledge</Button>
                                    </Card>
                                </Grid.Col>
                            </Grid>
                        </Stack>
                    </Tabs.Panel>
                </Tabs>
            </Paper>
        </Container>
    );
};

export default EscalationEnhanced;
